import { useCallback, useEffect, useRef } from "react";

export function useChatAutoScroll({
  itemCount,
  typingVisible = false,
  thresholdPx = 100,
  forceScrollKey = 0,
}) {
  const containerRef = useRef(null);
  const endRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const hasInitializedRef = useRef(false);
  const prevItemCountRef = useRef(itemCount);
  const prevTypingVisibleRef = useRef(typingVisible);
  const prevForceScrollKeyRef = useRef(forceScrollKey);
  const animationFrameRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isNearBottomRef.current = distanceFromBottom <= thresholdPx;
  }, [thresholdPx]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (animationFrameRef.current !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const runScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      if (typeof container.scrollTo === "function") {
        try {
          container.scrollTo({ top: container.scrollHeight, behavior });
        } catch (_) {
          container.scrollTop = container.scrollHeight;
        }
      } else {
        container.scrollTop = container.scrollHeight;
      }

      isNearBottomRef.current = true;
      animationFrameRef.current = null;
    };

    if (typeof requestAnimationFrame === "function") {
      animationFrameRef.current = requestAnimationFrame(runScroll);
    } else {
      runScroll();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const previousItemCount = prevItemCountRef.current;
    const previousTypingVisible = prevTypingVisibleRef.current;
    const previousForceScrollKey = prevForceScrollKeyRef.current;

    const hasInitialContent = itemCount > 0 || typingVisible;
    const itemCountIncreased = itemCount > previousItemCount;
    const typingJustAppeared = typingVisible && !previousTypingVisible;
    const forceScrollChanged = forceScrollKey !== previousForceScrollKey;

    if (!hasInitializedRef.current) {
      if (hasInitialContent) {
        hasInitializedRef.current = true;
        scrollToBottom("auto");
      }
    } else if (forceScrollChanged) {
      scrollToBottom("auto");
    } else if ((itemCountIncreased || typingJustAppeared) && isNearBottomRef.current) {
      scrollToBottom("smooth");
    }

    prevItemCountRef.current = itemCount;
    prevTypingVisibleRef.current = typingVisible;
    prevForceScrollKeyRef.current = forceScrollKey;
  }, [itemCount, typingVisible, forceScrollKey, scrollToBottom]);

  return {
    containerRef,
    endRef,
    handleScroll,
    scrollToBottom,
  };
}
